import { useEffect, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";

// Set the worker source for pdfjs-dist v3.x
pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

type PageSize = { width: number; height: number };

export function usePdfLoader(url: string) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageSize, setPageSize] = useState<PageSize | null>(null);
  const [pageTexts, setPageTexts] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!url) return;
      
      setLoading(true);
      setError(null);
      setPdf(null);
      setNumPages(0);
      setPageSize(null);
      setPageTexts({});

      try {
        const loadingTask = pdfjsLib.getDocument(url);
        const pdfDoc = await loadingTask.promise;

        if (cancelled) return;

        setPdf(pdfDoc);
        setNumPages(pdfDoc.numPages);

        // Get natural page size from page 1
        const page1 = await pdfDoc.getPage(1);
        if (!cancelled) {
          const viewport = page1.getViewport({ scale: 1 });
          setPageSize({ width: viewport.width, height: viewport.height });
        }

        // Extract text from all pages for search functionality
        const texts: Record<number, string> = {};
        for (let i = 1; i <= pdfDoc.numPages; i++) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(i);
          const content = await page.getTextContent();
          texts[i] = content.items
            .map((item) => ("str" in item ? item.str : ""))
            .join(" ");
        }
        
        if (!cancelled) {
          setPageTexts(texts);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return {
    pdf,
    numPages,
    pageSize,
    pageTexts,
    loading,
    error,
  };
}
